'use client';

import { useCallback, useEffect, useState } from 'react';

export const REMEMBERED_EMAIL_STORAGE_KEY = 'statura.auth.rememberedEmail.v1';

export function normalizeRememberedEmail(email: string): string {
  return email.trim().toLowerCase();
}

type RememberedEmailStorage = Pick<Storage, 'setItem' | 'removeItem'>;

export function persistRememberedEmailValue(
  storage: RememberedEmailStorage,
  storageKey: string,
  email: string,
  shouldRemember: boolean,
): string {
  const normalized = normalizeRememberedEmail(email);
  if (shouldRemember && normalized) {
    storage.setItem(storageKey, normalized);
    return normalized;
  }
  storage.removeItem(storageKey);
  return '';
}

export function useRememberedEmail(storageKey = REMEMBERED_EMAIL_STORAGE_KEY) {
  const [rememberEmail, setRememberEmail] = useState(false);
  const [rememberedEmail, setRememberedEmail] = useState('');

  useEffect(() => {
    try {
      const stored = normalizeRememberedEmail(window.localStorage.getItem(storageKey) ?? '');
      if (stored) {
        setRememberedEmail(stored);
        setRememberEmail(true);
      }
    } catch {
      // Storage can be blocked in private or hardened browser modes.
    }
  }, [storageKey]);

  const persistRememberedEmail = useCallback(
    (email: string, shouldRemember = rememberEmail) => {
      try {
        setRememberedEmail(
          persistRememberedEmailValue(window.localStorage, storageKey, email, shouldRemember),
        );
      } catch {
        // Remembering an email is optional and must never block authentication.
      }
    },
    [rememberEmail, storageKey],
  );

  const updateRememberEmail = useCallback(
    (checked: boolean) => {
      setRememberEmail(checked);
      if (!checked) persistRememberedEmail('', false);
    },
    [persistRememberedEmail],
  );

  return {
    rememberEmail,
    rememberedEmail,
    setRememberEmail: updateRememberEmail,
    persistRememberedEmail,
  };
}
